import { MarketPricesChartProps, MarketPricesWrapperProps } from 'components/charts/market-prices/types.ts'
import { BarElement, CategoryScale, ChartData, Chart as ChartJS, LinearScale, PointElement } from 'chart.js'
import { yScaleTitle } from 'components/charts/plugins.ts'
import useWindowSize from 'hooks/useWindowSize.tsx'
import dayjs from 'dayjs'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { getMarketPricesChartDatasets } from 'components/charts/market-prices/chartjs.config.ts'
import MarketPricesChartMobile from 'components/charts/market-prices/ChartMobile.tsx'
import MarketPricesChartDesktop from 'components/charts/market-prices/ChartDesktop.tsx'
import styles from 'components/charts/Charts.module.scss'
import Legend from 'components/charts/legend/Legend.tsx'
import { DSChartColors, DSFunctionalColors } from 'types/colors.ts'
import { formatAmount } from 'utils/format.ts'
import { useTranslation } from 'react-i18next'
import { useStoreSelector } from 'hooks/store.ts'
import classNames from 'classnames'
import { selectIsProducerAccount } from 'store/contact/slice.ts'
import ChartLayout from 'components/charts/chart-layout/ChartLayout.tsx'
import { formatPeriodLabel } from 'components/charts/price-history/utils.ts'
import { getMarketPriceBarColor } from 'components/charts/market-prices/utils.ts'

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, yScaleTitle)

const MarketPricesChart = ({
  data,
  granularity,
  lowestPriceIndexes,
  highestPriceIndexes,
  currentPriceIndex,
  isCompact,
  layoutClassName
}: MarketPricesWrapperProps) => {
  // Redux
  const isProducer = useStoreSelector(selectIsProducerAccount)

  // i18n
  const { t } = useTranslation(['marketPrices', 'common'])

  // Custom hooks
  const { isTablet } = useWindowSize()

  // Local state
  const [activeDataPointIndex, setActiveDataPointIndex] = useState<number | null>(null)

  // Memoized constants
  const labels = useMemo<string[]>(() => data.map(({ date }) => dayjs(date).toISOString()), [data])

  // Chart data
  const chartData = useMemo<ChartData<'bar', (number | null)[]>>(
    () => ({
      labels,
      datasets: getMarketPricesChartDatasets({
        labels,
        data,
        lowestIndexes: lowestPriceIndexes,
        currentIndex: currentPriceIndex,
        compact: !!isCompact,
        isProducer
      })
    }),
    [data, labels, lowestPriceIndexes, currentPriceIndex, isCompact, isProducer]
  )

  const currentEntry = data[currentPriceIndex]

  /**
   * Returns the legend entry of a single period, with an indicator following the color of its bar
   * @param {number} index
   * @param {boolean} isHovered
   * @returns {ReactElement}
   */
  const getPeriodEntry = (index: number, isHovered?: boolean): ReactElement => (
    <Legend.Entry
      label={formatPeriodLabel(data[index].date, granularity)}
      indicator={{
        color: getMarketPriceBarColor({ index, lowestIndexes: lowestPriceIndexes, currentIndex: currentPriceIndex, isProducer, isHovered }),
        full: true
      }}
    >
      <data value={data[index].price}>
        {t(($) => $.units.currencyPerKwh, {
          value: formatAmount(data[index].price, { fractionDigits: 3 }),
          ns: 'common'
        })}
      </data>
    </Legend.Entry>
  )

  // Props to pass
  const chartProps: MarketPricesChartProps = {
    chartData,
    granularity,
    labels,
    setHoverDataPointIndex: setActiveDataPointIndex,
    isCompact
  }

  // Reset active data point index if data is not available
  useEffect(() => {
    if (typeof activeDataPointIndex === 'number' && !data[activeDataPointIndex]) {
      setActiveDataPointIndex(null)
    }
  }, [activeDataPointIndex, data])

  return (
    <ChartLayout className={layoutClassName}>
      {/* CHART */}
      <div className={classNames(styles['chart-wrapper'], { [styles.compact]: isCompact })}>
        {isTablet || isCompact ? <MarketPricesChartMobile {...chartProps} /> : <MarketPricesChartDesktop {...chartProps} />}
      </div>

      {/* LEGEND */}
      <Legend boxed={!isCompact}>
        {typeof activeDataPointIndex === 'number' && !!data[activeDataPointIndex] ? (
          getPeriodEntry(activeDataPointIndex, true)
        ) : (
          <>
            {/* CURRENT PERIOD */}
            {!!currentEntry && getPeriodEntry(currentPriceIndex)}

            {isProducer ? (
              <>
                <Legend.Entry
                  label={t(($) => $.totals.highestPrice)}
                  indicator={{
                    icon: 'arrowRightTop',
                    color: DSFunctionalColors.POSITIVE
                  }}
                >
                  <strong>
                    {t(($) => $.units.currencyPerKwh, {
                      value: formatAmount(data[highestPriceIndexes[0]]?.price, { fractionDigits: 3 }),
                      ns: 'common'
                    })}
                  </strong>
                </Legend.Entry>

                <Legend.Entry
                  label={t(($) => $.totals.lowestPrice)}
                  indicator={{
                    icon: 'arrowRightBottom',
                    color: DSFunctionalColors.NEGATIVE
                  }}
                >
                  <strong>
                    {t(($) => $.units.currencyPerKwh, {
                      value: formatAmount(data[lowestPriceIndexes[0]]?.price, { fractionDigits: 3 }),
                      ns: 'common'
                    })}
                  </strong>
                </Legend.Entry>
              </>
            ) : (
              <>
                <Legend.Entry
                  label={t(($) => $.totals.lowestPrice)}
                  indicator={{
                    color: DSChartColors.GREEN,
                    full: true
                  }}
                >
                  <strong>
                    {t(($) => $.units.currencyPerKwh, {
                      value: formatAmount(Math.min(...data.map((entry) => entry.price)), { fractionDigits: 3 }),
                      ns: 'common'
                    })}
                  </strong>
                </Legend.Entry>

                <Legend.Entry label={t(($) => $.totals.averagePrice)} indicator={{ color: DSChartColors.TRANSPARENT, full: true }}>
                  <strong>
                    {t(($) => $.units.currencyPerKwh, {
                      value: formatAmount(data.reduce((total: number, curr) => total + curr.price, 0) / data.length, { fractionDigits: 3 }),
                      ns: 'common'
                    })}
                  </strong>
                </Legend.Entry>
              </>
            )}
          </>
        )}
      </Legend>
    </ChartLayout>
  )
}

export default MarketPricesChart
